import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { useGetAuthApi, usePutAuthApi } from '@/hooks/UseApiCalls';
import { AccountDetailsDtoIn } from '@/models/in/AccountDetailsDtoIn';
import { toLocalPhone } from '@/helpers/phoneHelper';
import { isValidIsraeliID } from '@/helpers/israeliIdValidator';
import Input from '@/components/ui/Input';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import styles from './UserSettings.module.css';

const ACCOUNT_URL = 'users/get';

/** Kept in step with UserService.MinimumPasswordLength, which is what actually enforces it. */
const MINIMUM_PASSWORD_LENGTH = 8;

/**
 * A shape check, to catch a typo next to the field instead of after a round trip. Deliberately
 * loose: the server's PhoneHelper.NormalizePhoneNumber is the authority, and a client stricter
 * than it locks people out of their own screen.
 *
 * That is exactly what an earlier version did. It matched against phoneHelper's prefix lists,
 * which cover 05x and 02/03/04/08/09 - the sets the fund forms offer in a dropdown - and so
 * rejected the 072/073/074/076/077 numbers the server accepts and stores. A coordinator with one
 * of those could not save even a corrected surname.
 */
const isPlausiblePhone = (phone: string): boolean => {
  const digits = toLocalPhone(phone).replace(/\D/g, '');
  if (!digits) return false;

  // Not an Israeli local number - the server knows about other countries, this screen does not.
  if (!digits.startsWith('0')) return true;

  // Israeli local numbers are nine digits (landline) or ten (mobile and 07x).
  return digits.length === 9 || digits.length === 10;
};

/**
 * מה שאפשר לשנות בחשבון של המשתמש עצמו (HAT-1127, סבב ראשון).
 *
 * עד כאן לא היה באתר החדש שום מקום כזה: רכז שרצה להחליף סיסמה היה צריך להתנתק,
 * ללחוץ "שכחתי סיסמה" ולחכות למייל, ושם או טלפון שגויים לא היו ניתנים לתיקון בכלל
 * אלא בפנייה למשרד. שני המקטעים כאן נשמרים בנפרד ובקריאות נפרדות, כי אין סיבה
 * שתיקון של שם משפחה יבקש סיסמה.
 *
 * מייל ההתחברות מוצג ואינו ניתן לעריכה: הוא המפתח הראשי של טבלת המשתמשים ומופיע
 * בהפניות רבות (בקשות תמיכה, מיופי כוח, מיקוד דגלי תכונה), כך שהחלפתו היא הגירה
 * ולא שדה בטופס.
 *
 * הטלפון כן נערך, אבל שינוי שלו עובר דרך אישור מפורש - בהחלטת ראשת הצוות, ומסיבה
 * מדויקת: הטלפון הוא פרטי התחברות ברגע שהכניסה בקוד קולי נדלקת, כי
 * VoiceOtpLoginService מזהה משתמש לפי Users.PhoneNumber בלבד ובלי דגל "מאומת".
 * כרגע היא אינה קיימת (VoiceOtp.Enabled=false בכל הסביבות, ומפתחים אותה לנדרים
 * בלבד), ולכן אישור מדעת הוא ההגנה המתאימה. ביום שהיא תגיע, האישור הזה יפסיק
 * להספיק - ו-ThrowIfUnverifiedPhoneChange בשרת יעצור את המסלול הזה מעצמו ויאלץ
 * מעבר לאימות אמיתי, בלי שאף אחד יצטרך לזכור.
 */
const UserSettingsPage = () => {
  const { t } = useTranslation();
  const { currentUser, updateCurrentUserDetails } = useAuthContext();

  const { data: account } = useGetAuthApi<AccountDetailsDtoIn>(ACCOUNT_URL);

  return (
    <div className={styles.container}>
      <div>
        <h1 className={styles.pageTitle}>{t('userSettingsPage.title')}</h1>
        <p className={styles.pageSubtitle}>{t('userSettingsPage.subtitle')}</p>
      </div>

      <PersonalDetailsSection account={account} loginEmail={account?.email ?? currentUser?.email ?? ''} onSaved={updateCurrentUserDetails} />

      <IsraeliIdSection account={account} />

      <ChangePasswordSection />
    </div>
  );
};

interface PersonalDetailsForm {
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

const PersonalDetailsSection = ({
  account,
  loginEmail,
  onSaved,
}: {
  account?: AccountDetailsDtoIn;
  loginEmail: string;
  onSaved: (details: PersonalDetailsForm) => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<PersonalDetailsForm>({ firstName: '', lastName: '', phoneNumber: '' });
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof PersonalDetailsForm, string>>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasSaved, setHasSaved] = useState(false);
  const [isPhoneConfirmOpen, setIsPhoneConfirmOpen] = useState(false);

  const storedPhone = toLocalPhone(account?.phoneNumber);

  // הטופס ממתין לתשובת השרת: לפני שהיא מגיעה אין מה למלא, ואם ממלאים מ-currentUser
  // בלבד מקבלים ערכים שנשמרו בהתחברות ועשויים להיות ישנים.
  useEffect(() => {
    if (!account) return;
    setForm({
      firstName: account.firstName ?? '',
      lastName: account.lastName ?? '',
      // הטלפון נשמר מנורמל (+972...), והרכז מצפה לראות את הצורה שהוא הקליד.
      phoneNumber: toLocalPhone(account.phoneNumber),
    });
  }, [account]);

  const saveDetails = usePutAuthApi<void, object, PersonalDetailsForm & { email: string }>('users/update', {
    onSuccess: (_data, variables) => {
      setSaveError(null);
      setHasSaved(true);
      onSaved({
        firstName: variables.body!.firstName,
        lastName: variables.body!.lastName,
        phoneNumber: variables.body!.phoneNumber,
      });
      /*
       * הרשומה נקראת מחדש, ולא רק נשמרת. storedPhone נגזר מ-account, ובלי זה הוא
       * נשאר על הערך שלפני השמירה - כך שכל שמירה נוספת באותה כניסה למסך הייתה
       * פותחת שוב את דיאלוג האישור, ומצטטת בו מספר שכבר הוחלף.
       */
      void queryClient.invalidateQueries({ queryKey: [ACCOUNT_URL] });
    },
    onError: (error) => {
      setHasSaved(false);
      setSaveError(getDetailsErrorMessage(error, t));
    },
  });

  const handleChange = (field: keyof PersonalDetailsForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((previous) => ({ ...previous, [field]: event.target.value }));
    setFieldErrors((previous) => ({ ...previous, [field]: undefined }));
    setHasSaved(false);
    setSaveError(null);
  };

  const send = () => {
    setSaveError(null);
    saveDetails.mutate({
      params: {},
      body: {
        // השרת דוחה עדכון שהמייל בו אינו המייל של המחובר, ולכן זו זהות ולא בחירה.
        email: loginEmail,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phoneNumber: form.phoneNumber.trim(),
      },
    });
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    const errors: Partial<Record<keyof PersonalDetailsForm, string>> = {};
    if (!form.firstName.trim()) errors.firstName = t('userSettingsPage.firstNameRequired');
    if (!form.lastName.trim()) errors.lastName = t('userSettingsPage.lastNameRequired');
    if (!form.phoneNumber.trim()) errors.phoneNumber = t('userSettingsPage.phoneRequired');
    else if (!isPlausiblePhone(form.phoneNumber)) errors.phoneNumber = t('userSettingsPage.phoneInvalid');

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    /*
     * שינוי טלפון נעצר לאישור; תיקון שם עובר ישר. ההשוואה על הצורה המקומית של
     * שני הצדדים, אחרת "0524567890" מול "+972524567890" ייראה כשינוי גם כשלא
     * נגעו בשדה, וכל שמירת שם הייתה פותחת דיאלוג.
     */
    if (toLocalPhone(form.phoneNumber) !== storedPhone) {
      setIsPhoneConfirmOpen(true);
      return;
    }

    send();
  };

  return (
    <section className={styles.panel}>
      <h2 className={styles.sectionTitle}>{t('userSettingsPage.personalDetails')}</h2>

      <div className={styles.readonlyRow}>
        <span className={styles.readonlyLabel}>{t('userSettingsPage.loginEmail')}</span>
        <span className={`${styles.readonlyValue} ph-mask`}>{loginEmail}</span>
      </div>
      <p className={styles.readonlyNote}>{t('userSettingsPage.loginEmailNote')}</p>

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <div className={styles.fieldGrid}>
          <div className={styles.formField}>
            <label className={styles.fieldLabel} htmlFor="user-settings-first-name">
              {t('userSettingsPage.firstName')}
            </label>
            <Input
              id="user-settings-first-name"
              value={form.firstName}
              onChange={handleChange('firstName')}
              error={!!fieldErrors.firstName}
              autoComplete="given-name"
              aria-describedby={fieldErrors.firstName ? 'user-settings-first-name-error' : undefined}
            />
            {fieldErrors.firstName && (
              <p id="user-settings-first-name-error" role="alert" className={styles.fieldError}>
                {fieldErrors.firstName}
              </p>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel} htmlFor="user-settings-last-name">
              {t('userSettingsPage.lastName')}
            </label>
            <Input
              id="user-settings-last-name"
              value={form.lastName}
              onChange={handleChange('lastName')}
              error={!!fieldErrors.lastName}
              autoComplete="family-name"
              aria-describedby={fieldErrors.lastName ? 'user-settings-last-name-error' : undefined}
            />
            {fieldErrors.lastName && (
              <p id="user-settings-last-name-error" role="alert" className={styles.fieldError}>
                {fieldErrors.lastName}
              </p>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel} htmlFor="user-settings-phone">
              {t('userSettingsPage.phoneNumber')}
            </label>
            <Input
              id="user-settings-phone"
              type="tel"
              dir="ltr"
              value={form.phoneNumber}
              onChange={handleChange('phoneNumber')}
              error={!!fieldErrors.phoneNumber}
              autoComplete="tel-national"
              aria-describedby={fieldErrors.phoneNumber ? 'user-settings-phone-error' : 'user-settings-phone-note'}
            />
            {fieldErrors.phoneNumber && (
              <p id="user-settings-phone-error" role="alert" className={styles.fieldError}>
                {fieldErrors.phoneNumber}
              </p>
            )}
          </div>
        </div>

        <p id="user-settings-phone-note" className={styles.readonlyNote}>
          {t('userSettingsPage.phoneNote')}
        </p>

        <div className={styles.formActions}>
          <button type="submit" className={styles.primaryBtn} disabled={saveDetails.isPending || !account}>
            {saveDetails.isPending ? t('userSettingsPage.savingDetails') : t('userSettingsPage.saveDetails')}
          </button>
          {hasSaved && (
            <p role="status" className={styles.formSuccess}>
              {t('userSettingsPage.detailsSaved')}
            </p>
          )}
          {saveError && (
            <p role="alert" className={styles.formError}>
              {saveError}
            </p>
          )}
        </div>
      </form>

      {/* אישור מדעת לשינוי הטלפון. הדיאלוג נוקב במספר היוצא ובמספר הנכנס, כי
          "אתה בטוח?" בלי הערכים לא נותן למשתמש שום דבר לבדוק מולו. */}
      <ConfirmationDialog
        open={isPhoneConfirmOpen}
        onOpenChange={setIsPhoneConfirmOpen}
        title={t('userSettingsPage.phoneConfirmTitle')}
        message={t('userSettingsPage.phoneConfirmMessage', {
          from: storedPhone || t('userSettingsPage.phoneNoneYet'),
          to: toLocalPhone(form.phoneNumber),
        })}
        confirmText={t('userSettingsPage.phoneConfirmAction')}
        cancelText={t('cancel')}
        isLoading={saveDetails.isPending}
        withIcon
        onConfirm={() => {
          setIsPhoneConfirmOpen(false);
          send();
        }}
      />
    </section>
  );
};

/**
 * מספר הזהות של הרכז. בלעדיו המשרד לא יכול למנות אותו כמייסד של הפרויקט, ובפרויקט
 * בלי מייסד אף אחד לא מוסיף רכזים ולא משנה הרשאות: «מינוי מייסדים» מזהה רכז לפי
 * המספר הזה בלבד, ועד עכשיו רק טופס פתיחת הפרויקט שמר אותו.
 *
 * מקטע נפרד עם שמירה משלו, ולא שדה רביעי בפרטים האישיים: הוא נשמר בקריאה אחרת
 * (users/israeli-id), כי את users/update קורא גם האתר הישן. ואחרי שהמשרד מינה את הרכז
 * לפי המספר, הוא מוצג לקריאה בלבד - מאותו רגע תיקון שלו הוא עניין של המשרד.
 */
const IsraeliIdSection = ({ account }: { account?: AccountDetailsDtoIn }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [israeliId, setIsraeliId] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasSaved, setHasSaved] = useState(false);

  const storedId = account?.israeliId ?? '';

  useEffect(() => {
    if (!account) return;
    setIsraeliId(account.israeliId ?? '');
  }, [account]);

  const saveIsraeliId = usePutAuthApi<void, object, { israeliId: string }>('users/israeli-id', {
    onSuccess: () => {
      setSaveError(null);
      setHasSaved(true);
      void queryClient.invalidateQueries({ queryKey: [ACCOUNT_URL] });
    },
    onError: (error) => {
      setHasSaved(false);
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        setFieldError(t('userSettingsPage.israeliIdInvalid'));
        return;
      }
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        setFieldError(t('userSettingsPage.israeliIdTaken'));
        return;
      }
      // 403: בינתיים המשרד מינה את הרכז לפי המספר שכבר שמור. הרשומה נקראת מחדש, והמקטע
      // עובר לקריאה בלבד עם ההסבר.
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        void queryClient.invalidateQueries({ queryKey: [ACCOUNT_URL] });
        return;
      }
      setSaveError(t('userSettingsPage.israeliIdError'));
    },
  });

  if (account?.israeliIdLocked && storedId) {
    return (
      <section className={styles.panel}>
        <h2 className={styles.sectionTitle}>{t('userSettingsPage.israeliIdTitle')}</h2>
        <div className={styles.readonlyRow}>
          <span className={styles.readonlyLabel}>{t('userSettingsPage.israeliIdLabel')}</span>
          <span className={`${styles.readonlyValue} ph-mask`} dir="ltr">
            {storedId}
          </span>
        </div>
        <p className={styles.readonlyNote}>{t('userSettingsPage.israeliIdLockedNote')}</p>
      </section>
    );
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const value = israeliId.trim();
    if (!value) {
      setFieldError(t('userSettingsPage.israeliIdRequired'));
      return;
    }
    if (!isValidIsraeliID(value)) {
      setFieldError(t('userSettingsPage.israeliIdInvalid'));
      return;
    }
    setFieldError(null);
    setSaveError(null);
    saveIsraeliId.mutate({ params: {}, body: { israeliId: value.padStart(9, '0') } });
  };

  return (
    <section className={styles.panel}>
      <h2 className={styles.sectionTitle}>{t('userSettingsPage.israeliIdTitle')}</h2>
      <p className={styles.readonlyNote}>{t('userSettingsPage.israeliIdNote')}</p>

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <div className={styles.singleFieldGrid}>
          <div className={styles.formField}>
            <label className={styles.fieldLabel} htmlFor="user-settings-israeli-id">
              {t('userSettingsPage.israeliIdLabel')}
            </label>
            <Input
              id="user-settings-israeli-id"
              className="ph-mask"
              dir="ltr"
              inputMode="numeric"
              maxLength={9}
              value={israeliId}
              onChange={(event) => {
                setIsraeliId(event.target.value.replace(/\D/g, ''));
                setFieldError(null);
                setSaveError(null);
                setHasSaved(false);
              }}
              error={!!fieldError}
              autoComplete="off"
              aria-describedby={fieldError ? 'user-settings-israeli-id-error' : undefined}
            />
            {fieldError && (
              <p id="user-settings-israeli-id-error" role="alert" className={styles.fieldError}>
                {fieldError}
              </p>
            )}
          </div>
        </div>

        <div className={styles.formActions}>
          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={saveIsraeliId.isPending || !account || israeliId.trim().padStart(9, '0') === storedId.padStart(9, '0')}
          >
            {saveIsraeliId.isPending ? t('userSettingsPage.savingDetails') : t('userSettingsPage.israeliIdSave')}
          </button>
          {hasSaved && (
            <p role="status" className={styles.formSuccess}>
              {t('userSettingsPage.israeliIdSaved')}
            </p>
          )}
          {saveError && (
            <p role="alert" className={styles.formError}>
              {saveError}
            </p>
          )}
        </div>
      </form>
    </section>
  );
};

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const emptyPasswordForm: PasswordForm = { currentPassword: '', newPassword: '', confirmPassword: '' };

const ChangePasswordSection = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState<PasswordForm>(emptyPasswordForm);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof PasswordForm, string>>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasSaved, setHasSaved] = useState(false);

  const changePassword = usePutAuthApi<void, object, { currentPassword: string; newPassword: string }>('users/change-password', {
    onSuccess: () => {
      setSaveError(null);
      setHasSaved(true);
      // אין טעם להשאיר סיסמאות בטופס אחרי שנשמרו.
      setForm(emptyPasswordForm);
    },
    onError: (error) => {
      setHasSaved(false);
      // 403 הוא התשובה היחידה שהמסך יכול לתלות בשדה מסוים, ולכן היא מוצגת שם
      // ולא כשגיאה כללית: "הסיסמה הנוכחית שגויה" הוא בדיוק מה שצריך לתקן.
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        setFieldErrors((previous) => ({ ...previous, currentPassword: t('userSettingsPage.currentPasswordWrong') }));
        return;
      }
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        setFieldErrors((previous) => ({ ...previous, newPassword: t('userSettingsPage.newPasswordTooShort') }));
        return;
      }
      // 429: יותר מדי ניסיונות שגויים בסיסמה הנוכחית. לא נתלה בשדה מסוים, כי
      // התשובה היא לחכות ולא להקליד משהו מחדש.
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        setSaveError(t('userSettingsPage.tooManyAttempts'));
        return;
      }
      setSaveError(t('userSettingsPage.passwordError'));
    },
  });

  const handleChange = (field: keyof PasswordForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((previous) => ({ ...previous, [field]: event.target.value }));
    setFieldErrors((previous) => ({ ...previous, [field]: undefined }));
    setHasSaved(false);
    setSaveError(null);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    const errors: Partial<Record<keyof PasswordForm, string>> = {};
    if (!form.currentPassword) errors.currentPassword = t('userSettingsPage.currentPasswordRequired');
    if (form.newPassword.length < MINIMUM_PASSWORD_LENGTH) errors.newPassword = t('userSettingsPage.newPasswordTooShort');
    if (form.newPassword !== form.confirmPassword) errors.confirmPassword = t('userSettingsPage.passwordsDoNotMatch');

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaveError(null);
    changePassword.mutate({
      params: {},
      body: { currentPassword: form.currentPassword, newPassword: form.newPassword },
    });
  };

  return (
    <section className={styles.panel}>
      <h2 className={styles.sectionTitle}>{t('userSettingsPage.changePassword')}</h2>

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {/* שלושת השדות באותו רשת אחת ולא הנוכחית לחוד: שדה בודד נמתח לרוחב אחר
            מזה של השדות שמתחתיו ומזה של הפרטים האישיים שמעל, ושלוש שורות ברוחב
            שונה במסך אחד נראות כמו תקלה. */}
        <div className={styles.fieldGrid}>
          <div className={styles.formField}>
            <label className={styles.fieldLabel} htmlFor="user-settings-current-password">
              {t('userSettingsPage.currentPassword')}
            </label>
            <Input
              id="user-settings-current-password"
              type="password"
              value={form.currentPassword}
              onChange={handleChange('currentPassword')}
              error={!!fieldErrors.currentPassword}
              autoComplete="current-password"
              aria-describedby={fieldErrors.currentPassword ? 'user-settings-current-password-error' : undefined}
            />
            {fieldErrors.currentPassword && (
              <p id="user-settings-current-password-error" role="alert" className={styles.fieldError}>
                {fieldErrors.currentPassword}
              </p>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel} htmlFor="user-settings-new-password">
              {t('userSettingsPage.newPassword')}
            </label>
            <Input
              id="user-settings-new-password"
              type="password"
              value={form.newPassword}
              onChange={handleChange('newPassword')}
              error={!!fieldErrors.newPassword}
              autoComplete="new-password"
              aria-describedby={fieldErrors.newPassword ? 'user-settings-new-password-error' : undefined}
            />
            {fieldErrors.newPassword && (
              <p id="user-settings-new-password-error" role="alert" className={styles.fieldError}>
                {fieldErrors.newPassword}
              </p>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel} htmlFor="user-settings-confirm-password">
              {t('userSettingsPage.confirmPassword')}
            </label>
            <Input
              id="user-settings-confirm-password"
              type="password"
              value={form.confirmPassword}
              onChange={handleChange('confirmPassword')}
              error={!!fieldErrors.confirmPassword}
              autoComplete="new-password"
              aria-describedby={fieldErrors.confirmPassword ? 'user-settings-confirm-password-error' : undefined}
            />
            {fieldErrors.confirmPassword && (
              <p id="user-settings-confirm-password-error" role="alert" className={styles.fieldError}>
                {fieldErrors.confirmPassword}
              </p>
            )}
          </div>
        </div>

        <div className={styles.formActions}>
          <button type="submit" className={styles.primaryBtn} disabled={changePassword.isPending}>
            {changePassword.isPending ? t('userSettingsPage.savingPassword') : t('userSettingsPage.savePassword')}
          </button>
          {hasSaved && (
            <p role="status" className={styles.formSuccess}>
              {t('userSettingsPage.passwordSaved')}
            </p>
          )}
          {saveError && (
            <p role="alert" className={styles.formError}>
              {saveError}
            </p>
          )}
        </div>
      </form>
    </section>
  );
};

/**
 * 400 מ-users/update כאן פירושו טלפון שמור שהשרת לא הצליח לנרמל. השדה אינו נערך
 * במסך, ולכן זה לא משהו שהמשתמש הקליד ולא משהו שהוא יכול לתקן מכאן - ההודעה
 * מפנה למשרד ולא מאשימה את הטופס. 409 (טלפון של חשבון אחר) אינו אפשרי יותר:
 * הערך נשלח כפי שהוא, והבדיקה בשרת מדלגת על החשבון של הקורא עצמו.
 */
const getDetailsErrorMessage = (error: unknown, t: (key: string) => string): string => {
  if (axios.isAxiosError(error)) {
    // 409: מספר שכבר שייך לחשבון אחר (PhoneNumberAlreadyInUseException). לא ניתן
    // לצפות בטופס, כי רק השרת רואה את שאר החשבונות.
    if (error.response?.status === 409) return t('userSettingsPage.phoneTaken');
    // 403: הכניסה בקוד קולי נדלקה, ומאותו רגע שינוי טלפון מחייב אימות של המספר
    // החדש (ThrowIfUnverifiedPhoneChange). הסטטוס נפרד מ-409 בכוונה - כששניהם
    // חלקו אותו קוד, המסך אמר למשתמש שהמספר החדש שלו שייך למישהו אחר.
    if (error.response?.status === 403) return t('userSettingsPage.phoneNeedsVerification');
    // 400: השרת לא הצליח לנרמל את המספר. הוולידציה כאן מקדימה את רוב המקרים אבל
    // היא רופפת בכוונה, ולכן לא את כולם.
    if (error.response?.status === 400) return t('userSettingsPage.phoneInvalid');
  }

  return t('userSettingsPage.detailsError');
};

export default UserSettingsPage;
